/**
 * API Client — Base HTTP client with JWT auth interceptor.
 *
 * All REST calls to /api/v1/* go through this client.
 * Handles token refresh automatically on 401s.
 */

import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosError } from 'axios';
import { getApiBaseUrl } from '../utils/constants';
import * as storage from '../utils/storage';
import {
  createSessionExpiredError,
  isDefinitiveAuthFailure,
  shouldRetryWithRotatedToken,
} from '../utils/authErrors';
import type {
  ChatCommandResponse,
  PaymentCaptureContextResponse,
  PaymentCompletionResponse,
  PaymentOrderResponse,
  SubscriptionBundlesResponse,
  SubscriptionDetailsResponse,
  AutoRenewResponse,
  CancelSubscriptionResponse,
  CardUpdateCompletionResponse,
  PublicSubscriptionStartResponse,
  PublicSubscriptionVerifyResponse,
  PublicSubscriptionCheckoutIntentResponse,
} from '../types/api';

let _client: AxiosInstance | null = null;
let _refreshPromise: Promise<{ accessToken: string; refreshToken: string }> | null = null;
let _onSessionExpired: (() => void) | null = null;

/**
 * Register a callback that fires when the backend rejects token refresh.
 * Network/server errors do not fire this callback, so offline sessions remain
 * available while connectivity is transiently unavailable.
 */
export function setSessionExpiredHandler(handler: () => void): void {
  _onSessionExpired = handler;
}

export function resetClient(): void {
  _client = null;
  _refreshPromise = null;
}

async function performTokenRefresh(): Promise<{ accessToken: string; refreshToken: string }> {
  if (_refreshPromise) {
    return _refreshPromise;
  }

  _refreshPromise = (async () => {
    const refreshToken = await storage.getRefreshToken();
    if (!refreshToken) {
      throw createSessionExpiredError('No refresh token');
    }

    const refresh = async (token: string) => {
      const { data } = await axios.post(`${getApiBaseUrl()}/api/v1/auth/refresh`, {
        refreshToken: token,
      });

      await storage.setAccessToken(data.accessToken);
      if (data.refreshToken) {
        await storage.setRefreshToken(data.refreshToken);
      }

      return data as { accessToken: string; refreshToken: string };
    };

    try {
      return await refresh(refreshToken);
    } catch (error) {
      if (!isDefinitiveAuthFailure(error)) throw error;

      // A headless background task can refresh in a separate JS runtime. Give
      // that winner a moment to persist its rotated token, then retry with it
      // instead of treating the losing 401 as a logged-out session.
      let latestToken = await storage.getRefreshToken();
      if (!shouldRetryWithRotatedToken(error, refreshToken, latestToken)) {
        await new Promise(resolve => setTimeout(resolve, 150));
        latestToken = await storage.getRefreshToken();
      }
      if (shouldRetryWithRotatedToken(error, refreshToken, latestToken)) {
        return refresh(latestToken);
      }
      throw error;
    }
  })();

  try {
    return await _refreshPromise;
  } catch (refreshError: any) {
    if (isDefinitiveAuthFailure(refreshError)) {
      _onSessionExpired?.();
    }

    throw refreshError;
  } finally {
    _refreshPromise = null;
  }
}

export function getClient(): AxiosInstance {
  if (_client) return _client;

  const baseURL = getApiBaseUrl();
  console.log('[API] Creating client with baseURL:', baseURL);
  _client = axios.create({
    baseURL,
    timeout: 15000,
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '1',
    },
  });

  // Request interceptor: attach Bearer token
  _client.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = await storage.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // Response interceptor: refresh on 401
  _client.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

      if (error.response?.status === 401 && !originalRequest._retry) {
        originalRequest._retry = true;

        try {
          const data = await performTokenRefresh();
          originalRequest.headers.Authorization = `Bearer ${data.accessToken}`;
          return _client!.request(originalRequest);
        } catch (refreshError) {
          throw refreshError;
        }
      }

      return Promise.reject(error);
    }
  );

  return _client;
}

// ============================================================
// AUTH (no auth needed)
// ============================================================

export async function requestOtp(phone: string): Promise<{ success: boolean; isTestAccount?: boolean }> {
  const { data } = await axios.post(`${getApiBaseUrl()}/api/v1/auth/request-otp`, { phone });
  return data;
}

export async function verifyOtp(params: {
  phone: string;
  otp: string;
  deviceId: string;
  platform: string;
  pushToken?: string;
}) {
  const { data } = await axios.post(`${getApiBaseUrl()}/api/v1/auth/verify-otp`, params);
  return data as { accessToken: string; refreshToken: string; user: any };
}

export async function refreshAccessToken(_refreshToken?: string) {
  return performTokenRefresh();
}

export async function logout(deviceId?: string) {
  return getClient().post('/api/v1/auth/logout', { deviceId });
}

// ============================================================
// CHAT
// ============================================================

export async function getChatHistory(params?: { before?: string; limit?: number }) {
  const { data } = await getClient().get('/api/v1/chat/history', { params });
  return data as { messages: any[] };
}

export async function clearChatHistory() {
  const { data } = await getClient().delete('/api/v1/chat/history');
  return data as { ok: boolean; deleted: number };
}

export async function sendChatMessage(text: string): Promise<ChatCommandResponse> {
  const { data } = await getClient().post('/api/v1/chat/send', { text });
  return data as ChatCommandResponse;
}

export async function sendButtonPress(buttonId: string): Promise<ChatCommandResponse> {
  const { data } = await getClient().post('/api/v1/chat/button', { buttonId });
  return data as ChatCommandResponse;
}

export async function sendListSelect(listItemId: string): Promise<ChatCommandResponse> {
  const { data } = await getClient().post('/api/v1/chat/list-select', { listItemId });
  return data as ChatCommandResponse;
}

export async function addReaction(messageId: number, emoji: string) {
  const { data } = await getClient().post('/api/v1/chat/react', { messageId, emoji });
  return data;
}

export async function removeReaction(messageId: number) {
  const { data } = await getClient().delete('/api/v1/chat/react', { data: { messageId } });
  return data;
}

// ============================================================
// REMINDERS
// ============================================================

export async function getReminders(params?: { filter?: string; tag?: string }) {
  const { data } = await getClient().get('/api/v1/reminders', { params });
  return data as { reminders: any[] };
}

export async function getReminder(seq: number) {
  const { data } = await getClient().get(`/api/v1/reminders/${seq}`);
  return data as { reminder: any };
}

export async function updateReminder(seq: number, updates: { body?: string; runAtUtc?: string; tags?: string; recurJson?: object | null; isRecurring?: boolean; durationMinutes?: number | null; colPref?: number | null }) {
  const { data } = await getClient().put(`/api/v1/reminders/${seq}`, updates);
  return data as { reminder: any };
}

export async function createReminder(params: { body: string; runAtUtc: string; tags?: string[]; durationMinutes?: number | null }) {
  const { data } = await getClient().post('/api/v1/reminders', params);
  return data as { reminder: any };
}

// 3-way delete: googleAction 'delete' also removes the synced Google event;
// omit (or 'keep') to delete from the app only and leave Google intact.
export async function deleteReminder(seq: number, opts?: { googleAction?: 'delete' | 'keep' }) {
  const qs = opts?.googleAction === 'delete' ? '?google=delete' : '';
  const { data } = await getClient().delete(`/api/v1/reminders/${seq}${qs}`);
  return data;
}

export async function deleteAllReminders() {
  const { data } = await getClient().delete('/api/v1/reminders');
  return data as { count: number };
}

export async function snoozeReminder(seq: number, minutes = 15, occurrenceRunAt?: string | null) {
  const { data } = await getClient().post(`/api/v1/reminders/${seq}/snooze`, {
    minutes,
    occurrenceRunAt: occurrenceRunAt || undefined,
  });
  return data as {
    reminder: any;
    snoozedReminder?: any;
    unchanged?: boolean;
    seriesUnchanged?: boolean;
  };
}

export async function pauseReminder(seq: number) {
  const { data } = await getClient().post(`/api/v1/reminders/${seq}/pause`);
  return data as { reminder: any };
}

export async function resumeReminder(seq: number) {
  const { data } = await getClient().post(`/api/v1/reminders/${seq}/resume`);
  return data as { reminder: any };
}

export async function skipReminder(seq: number) {
  const { data } = await getClient().post(`/api/v1/reminders/${seq}/skip`);
  return data as { reminder: any };
}

export async function markReminderDone(seq: number, occurrenceRunAt?: string | null) {
  const { data } = await getClient().post(`/api/v1/reminders/${seq}/done`, {
    occurrenceRunAt: occurrenceRunAt || undefined,
  });
  return data as { success: boolean; unchanged?: boolean; undoAvailable?: boolean; reminder?: any };
}

export async function undoReminderDone(seq: number) {
  const { data } = await getClient().post(`/api/v1/reminders/${seq}/undo-done`);
  return data as { success: boolean; reminder?: any; isOverdue?: boolean };
}

// ============================================================
// CALENDAR
// ============================================================

export async function getCalendarEvents(params?: { start?: string; end?: string }) {
  const { data } = await getClient().get('/api/v1/calendar/events', { params });
  return data;
}

export async function getTodayEvents() {
  const { data } = await getClient().get('/api/v1/calendar/today');
  return data;
}

// ============================================================
// USER / SETTINGS
// ============================================================

export async function getUserProfile() {
  const { data } = await getClient().get('/api/v1/user/profile');
  return data as { user: any };
}

export async function updateProfile(updates: { displayName?: string; lang?: string; tz?: string }) {
  const { data } = await getClient().put('/api/v1/user/profile', updates);
  return data as { user: any };
}

export async function updateSettings(updates: { autosyncGoogle?: boolean; defaultDurationMinutes?: number; defaultReminderTimeMinutes?: number; tagColors?: Record<string, string> }) {
  const { data } = await getClient().put('/api/v1/user/settings', updates);
  return data as { user: any };
}

// ============================================================
// PAYMENTS / SUBSCRIPTIONS
// ============================================================

export async function getSubscriptionBundles(): Promise<SubscriptionBundlesResponse> {
  const { data } = await getClient().get('/api/v1/subscription/bundles');
  return data as SubscriptionBundlesResponse;
}

export async function createPaymentOrder(bundleId: number): Promise<PaymentOrderResponse> {
  const { data } = await getClient().post('/api/v1/payments/orders', { bundleId });
  return data as PaymentOrderResponse;
}

export async function createPaymentCheckoutIntent(bundleId: number): Promise<{ token: string; checkoutUrl: string; expiresAtUtc: string; bundle: any; payments?: any }> {
  const { data } = await getClient().post('/api/v1/payments/checkout-intent', { bundleId });
  return data;
}

export async function startPublicSubscription(phone: string): Promise<PublicSubscriptionStartResponse> {
  const { data } = await axios.post(`${getApiBaseUrl()}/api/v1/subscription/public/start`, { phone });
  return data as PublicSubscriptionStartResponse;
}

export async function verifyPublicSubscription(phone: string, otp: string): Promise<PublicSubscriptionVerifyResponse> {
  const { data } = await axios.post(`${getApiBaseUrl()}/api/v1/subscription/public/verify`, { phone, otp });
  return data as PublicSubscriptionVerifyResponse;
}

export async function getPublicSubscriptionBundles(): Promise<SubscriptionBundlesResponse> {
  const { data } = await axios.get(`${getApiBaseUrl()}/api/v1/subscription/public/bundles`);
  return data as SubscriptionBundlesResponse;
}

export async function createPublicSubscriptionCheckoutIntent(params: {
  token: string;
  bundleId: number;
}): Promise<PublicSubscriptionCheckoutIntentResponse> {
  const { data } = await axios.post(`${getApiBaseUrl()}/api/v1/subscription/public/checkout-intent`, params);
  return data as PublicSubscriptionCheckoutIntentResponse;
}

export async function createPaymentCaptureContext(orderId: string): Promise<PaymentCaptureContextResponse> {
  const { data } = await getClient().post(`/api/v1/payments/orders/${orderId}/capture-context`);
  return data as PaymentCaptureContextResponse;
}

export async function completePaymentOrder(orderId: string, transientTokenJwt: string, enableAutoRenew?: boolean): Promise<PaymentCompletionResponse> {
  const { data } = await getClient().post(`/api/v1/payments/orders/${orderId}/complete`, { transientTokenJwt, enableAutoRenew });
  return data as PaymentCompletionResponse;
}

export async function extendWithSavedCard(bundleId: number): Promise<PaymentCompletionResponse> {
  const { data } = await getClient().post('/api/v1/payments/extend-with-saved-card', { bundleId });
  return data as PaymentCompletionResponse;
}

export async function removeCard(): Promise<CancelSubscriptionResponse> {
  const { data } = await getClient().post('/api/v1/payments/card/remove');
  return data as CancelSubscriptionResponse;
}

export async function createPublicPaymentOrder(token: string): Promise<PaymentOrderResponse> {
  const { data } = await axios.post(`${getApiBaseUrl()}/api/v1/payments/public/orders`, { token });
  return data as PaymentOrderResponse;
}

export async function createPublicPaymentCaptureContext(orderId: string, token: string): Promise<PaymentCaptureContextResponse> {
  const { data } = await axios.post(`${getApiBaseUrl()}/api/v1/payments/public/orders/${orderId}/capture-context`, { token });
  return data as PaymentCaptureContextResponse;
}

export async function completePublicPaymentOrder(orderId: string, token: string, transientTokenJwt: string, enableAutoRenew?: boolean): Promise<PaymentCompletionResponse> {
  const { data } = await axios.post(`${getApiBaseUrl()}/api/v1/payments/public/orders/${orderId}/complete`, { token, transientTokenJwt, enableAutoRenew });
  return data as PaymentCompletionResponse;
}

// ── Subscription auto-renewal ──

export async function getSubscriptionDetails(): Promise<SubscriptionDetailsResponse> {
  const { data } = await getClient().get('/api/v1/subscription');
  return data as SubscriptionDetailsResponse;
}

export async function setAutoRenew(enabled: boolean): Promise<AutoRenewResponse> {
  const { data } = await getClient().post('/api/v1/subscription/auto-renew', { enabled });
  return data as AutoRenewResponse;
}

export async function cancelSubscription(): Promise<CancelSubscriptionResponse> {
  const { data } = await getClient().post('/api/v1/subscription/cancel');
  return data as CancelSubscriptionResponse;
}

export async function startCardUpdate(): Promise<PaymentCaptureContextResponse> {
  const { data } = await getClient().post('/api/v1/payments/card/update/start');
  return data as PaymentCaptureContextResponse;
}

export async function completeCardUpdate(orderId: string, transientTokenJwt: string, billingInfo?: object): Promise<CardUpdateCompletionResponse> {
  const { data } = await getClient().post('/api/v1/payments/card/update/complete', { orderId, transientTokenJwt, billingInfo });
  return data as CardUpdateCompletionResponse;
}

export async function setTimezone(tz: string) {
  const { data } = await getClient().post('/api/v1/user/timezone', { tz });
  return data as { user: any };
}

export async function detectTimezone(lat: number, lng: number) {
  const { data } = await getClient().post('/api/v1/user/timezone/detect', { lat, lng });
  return data as { tz: string; localTime: string };
}

export async function getTimezones(region?: string) {
  const { data } = await getClient().get('/api/v1/user/timezones', { params: { region } });
  return data as { timezones: string[] };
}

// ============================================================
// GOOGLE
// ============================================================

export async function getGoogleAuthUrl() {
  const { data } = await getClient().get('/api/v1/google/auth-url');
  return data as { url: string };
}

export async function getGoogleStatus() {
  const { data } = await getClient().get('/api/v1/google/status');
  return data as { connected: boolean; email?: string | null };
}

// events: 'remove' deletes FishMemory's events from Google; 'keep' (default)
// leaves them. Account-switch safety is handled server-side at reconnect.
export async function disconnectGoogle(events: 'keep' | 'remove' = 'keep') {
  const { data } = await getClient().delete(`/api/v1/google?events=${events}`);
  return data as { disconnected: boolean; removedEvents?: number };
}

export async function getGoogleSyncCounts() {
  const { data } = await getClient().get('/api/v1/google/sync-counts');
  return data as { synced: number; total: number; unsynced: number };
}

export async function syncAllGoogle() {
  const { data } = await getClient().post('/api/v1/google/sync-all');
  return data as { synced: number };
}

export async function importGoogleEvents() {
  const { data } = await getClient().post('/api/v1/google/import');
  return data as { imported: number; updated: number; skipped: number };
}

export async function syncReminderToGoogle(seq: number) {
  const { data } = await getClient().post(`/api/v1/google/sync/${seq}`);
  return data as { success: boolean; event?: unknown };
}

export async function unsyncReminderFromGoogle(seq: number) {
  const { data } = await getClient().post(`/api/v1/google/unsync/${seq}`);
  return data as { success: boolean; alreadyUnsynced?: boolean };
}

// ============================================================
// DEVICES
// ============================================================

export async function registerDevice(params: { pushToken?: string; platform: string; deviceId: string; appVersion?: string }) {
  const { data } = await getClient().post('/api/v1/devices/register', params);
  return data;
}

export async function updatePushToken(pushToken: string, deviceId: string) {
  const { data } = await getClient().put('/api/v1/devices/push-token', { pushToken, deviceId });
  return data;
}

// PUSH
// ============================================================

export async function getVapidPublicKey(): Promise<string | null> {
  try {
    const { data } = await getClient().get('/api/v1/push/vapid-public-key');
    return data?.vapidPublicKey || null;
  } catch {
    return null;
  }
}

// ============================================================
// LANGUAGES
// ============================================================

// Memoise so even pathological re-mount loops can't DoS the backend.
let _languagesCache: Array<{ code: string; name: string; nativeName: string; flag: string; rtl: boolean; tier: string }> | null = null;
let _languagesInflight: Promise<Array<{ code: string; name: string; nativeName: string; flag: string; rtl: boolean; tier: string }>> | null = null;
let _languagesLastFailAt = 0;

export async function fetchLanguages(): Promise<Array<{ code: string; name: string; nativeName: string; flag: string; rtl: boolean; tier: string }>> {
  if (_languagesCache) return _languagesCache;
  if (_languagesInflight) return _languagesInflight;
  // After a failure, back off for 30s before retrying so we never hot-loop.
  if (Date.now() - _languagesLastFailAt < 30_000) return [];
  _languagesInflight = (async () => {
    try {
      const { data } = await axios.get(`${getApiBaseUrl()}/api/v1/languages`);
      const list = Array.isArray(data) ? data : [];
      if (list.length > 0) _languagesCache = list;
      else _languagesLastFailAt = Date.now();
      return list;
    } catch {
      _languagesLastFailAt = Date.now();
      return [];
    } finally {
      _languagesInflight = null;
    }
  })();
  return _languagesInflight;
}

// PREFERENCES
// ============================================================

export async function getPreferences(): Promise<{ headsup_enabled: boolean; headsup_minutes: number; auto_done_on_fire: number; followup_enabled: number; followup_interval_minutes: number }> {
  const { data } = await getClient().get('/api/v1/users/me/preferences');
  return data;
}

export async function updatePreferences(updates: { headsup_enabled?: boolean; headsup_minutes?: number; auto_done_on_fire?: number; followup_enabled?: number; followup_interval_minutes?: number }) {
  const { data } = await getClient().put('/api/v1/users/me/preferences', updates);
  return data;
}

// ============================================================
// ACCOUNT MANAGEMENT
// ============================================================

export async function exportUserData() {
  const { data } = await getClient().get('/api/v1/user/export');
  return data;
}

export async function deleteAccount() {
  const { data } = await getClient().delete('/api/v1/user/account');
  return data;
}

export async function cancelDeletion() {
  const { data } = await getClient().post('/api/v1/user/cancel-deletion');
  return data;
}

export async function submitFeedback(message: string, type?: string) {
  const { data } = await getClient().post('/api/v1/feedback', { message, type });
  return data;
}

export async function getDevices(): Promise<{ devices: { device_id: string; platform: string; app_version: string; last_seen_utc: string; created_at_utc: string }[] }> {
  const { data } = await getClient().get('/api/v1/devices');
  return data;
}

export async function logoutOtherDevices(deviceId: string) {
  const { data } = await getClient().post('/api/v1/devices/logout-others', { deviceId });
  return data;
}

// ============================================================
// SUPPORT CHAT
// ============================================================

export async function openSupportSession(subject?: string) {
  const { data } = await getClient().post('/api/v1/support/sessions', { subject });
  return data as { session: any; isExisting: boolean; autoAssigned: boolean; afterHours: boolean };
}

export async function getActiveSupportSession() {
  const { data } = await getClient().get('/api/v1/support/sessions/active');
  return data as { session: any; messages: any[] };
}

export async function sendSupportMessage(sessionId: number, body: string) {
  const { data } = await getClient().post(`/api/v1/support/sessions/${sessionId}/messages`, { body });
  return data as { message: any };
}

export async function closeSupportSession(sessionId: number) {
  const { data } = await getClient().delete(`/api/v1/support/sessions/${sessionId}`);
  return data as { session: any };
}

export async function rateSupportSession(sessionId: number, rating: number) {
  const { data } = await getClient().post(`/api/v1/support/sessions/${sessionId}/rate`, { rating });
  return data;
}

export async function getSupportHours() {
  const { data } = await getClient().get('/api/v1/support/hours');
  return data as { withinHours: boolean; queueDepth: number };
}
